"use client";

import { useState, useMemo, useEffect, Fragment, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { getCountries, getCountryCallingCode, parsePhoneNumber, type Country } from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { Input } from "@/components/ui/input";
import { ChevronDown } from "lucide-react";

import { FormField, FormItem, FormLabel, FormMessage, useFormField } from "@/components/ui/form";
import { FONT_SIZES } from "@/components/ui/constants";
import { cn } from "@/lib/utils";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectSeparator,
} from "@/components/ui/select";
import { detectCountryFromGeolocation } from "@/lib/ip-geolocation";

import "react-phone-number-input/style.css";

type TextInputProps = {
	name: string;
	label: string;
	placeholder?: string;
	value?: string;
	disabled?: boolean;
	intent?: "column" | "inlined";
};

// Custom country selector component with flags, names, and codes
function CountrySelector({
	value,
	onChange,
	disabled,
}: {
	value: Country | undefined;
	onChange: (country: Country | undefined) => void;
	disabled?: boolean;
}) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");

	// Priority countries to show first (by country code)
	const priorityCountryCodes: Country[] = ["US", "GB", "CA", "AU"];

	const countries = useMemo(() => {
		const allCountries = getCountries();
		const priority = priorityCountryCodes.filter(code => allCountries.includes(code));
		const rest = allCountries.filter(code => !priorityCountryCodes.includes(code));
		return [...priority, ...rest];
	}, []);

	const filteredCountries = useMemo(() => {
		if (!search) return countries;
		const lowerSearch = search.toLowerCase();
		return countries.filter((country) => {
			const name = new Intl.DisplayNames(["en"], { type: "region" }).of(country) || "";
			const code = `+${getCountryCallingCode(country)}`;
			return name.toLowerCase().includes(lowerSearch) || code.includes(lowerSearch);
		});
	}, [countries, search]);

	const Flag = value ? flags[value] : null;

	return (
		<Select
			open={open}
			onOpenChange={setOpen}
			value={value || ""}
			onValueChange={(val) => {
				onChange(val ? (val as Country) : undefined);
				setOpen(false);
				setSearch("");
			}}
			disabled={disabled}
		>
			<SelectTrigger
				className={cn(
					"h-full rounded-l-md rounded-r-none border-0 bg-transparent pl-2 pr-1",
					"focus:ring-0 focus:ring-offset-0",
					"[&>span]:hidden", // Hide default SelectValue span
					"[&>svg]:hidden", // Hide default chevron icon from SelectPrimitive.Icon
					"[&>*:last-child]:hidden" // Hide the last child which is the SelectPrimitive.Icon
				)}
				style={{ 
					width: "auto", 
					borderRight: "1px solid var(--color-border-standard)"
				}}
			>
				<div className="flex items-center gap-1" style={{ marginRight: "0", width: "fit-content" }}>
					{Flag && value ? (
						<span
							className="phone-country-flag inline-flex shrink-0 items-center overflow-visible"
							style={{ width: "24px", height: "18px" }}
						>
							<Flag title={value} />
						</span>
					) : (
						<span className="shrink-0 text-xs text-muted-foreground">Select</span>
					)}
					<ChevronDown className="size-4 shrink-0 text-[var(--color-brand-primary)]" />
				</div>
			</SelectTrigger>
			<SelectContent className="max-h-[300px] w-[400px] [&>div]:!h-auto [&>div]:max-h-[300px] [&>div]:overflow-y-auto [&>div]:p-0">
				{/* Search input */}
				<div 
					className="border-standard sticky top-0 z-20 border-b p-2"
					style={{ 
						position: 'sticky',
						top: '-4px',
						zIndex: 20,
						backgroundColor: 'var(--color-bg-aqua-mist)',
						marginTop: '-4px',
						marginLeft: '-4px',
						marginRight: '-4px',
						paddingTop: '12px',
						paddingBottom: '12px',
					}}
				>
					<input
						type="text"
						placeholder="Search country"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						onClick={(e) => e.stopPropagation()}
						className={cn(
							"w-full rounded-md border border-standard bg-[var(--color-bg-aqua-mist)] dark:bg-[var(--color-core-deep-midnight)] px-3 py-2",
							FONT_SIZES.input,
							"text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)]"
						)}
					/>
				</div>
				{/* Country list */}
				{filteredCountries.map((country, index) => {
					const FlagComponent = flags[country];
					const countryName = new Intl.DisplayNames(["en"], { type: "region" }).of(country) || country;
					const callingCode = `+${getCountryCallingCode(country)}`;
					const isPriority = priorityCountryCodes.includes(country);
					const isLastPriority = isPriority && 
						(index === filteredCountries.length - 1 || 
						 !priorityCountryCodes.includes(filteredCountries[index + 1]));

					return (
						<Fragment key={country}>
							<SelectItem
								value={country}
								className="relative flex items-center gap-3 py-2 pl-8 [&>span:first-child]:hidden"
							>
								{/* Flag in checkmark position */}
								<span className="absolute left-2 flex size-3.5 items-center justify-center">
									{FlagComponent && (
										<span
											className="phone-country-flag inline-flex items-center"
											style={{ width: "20px", height: "15px" }}
										>
											<FlagComponent title={country} />
										</span>
									)}
								</span>
								{/* Country name and code */}
								<div className="ml-2 flex min-w-0 flex-1 items-center gap-3">
									<span className="flex-1 truncate text-left">{countryName}</span>
									<span className="shrink-0 text-muted-foreground">{callingCode}</span>
								</div>
							</SelectItem>
							{isLastPriority && index < filteredCountries.length - 1 && (
								<SelectSeparator />
							)}
						</Fragment>
					);
				})}
			</SelectContent>
		</Select>
	);
}

// Inner component to handle phone number state with hooks
function PhoneNumberInputInner({
	field,
	label,
	placeholder,
	intent,
	disabled,
}: {
	field: { value: string | undefined; onChange: (value: string) => void; disabled?: boolean };
	label: string;
	placeholder?: string;
	intent?: "column" | "inlined";
	disabled?: boolean;
}) {
	const hasUserSelectedCountry = useRef(false);
	const phoneNumberRef = useRef("");
	const fieldValueRef = useRef<string | undefined>(field.value);

	// Parse existing phone number to extract country and number
	const getInitialCountry = (): Country => {
		if (field.value) {
			try {
				const parsed = parsePhoneNumber(field.value as string);
				return parsed?.country || "US";
			} catch {
				return "US";
			}
		}
		return "US";
	};

	const getInitialNumber = (): string => {
		if (field.value) {
			try {
				const parsed = parsePhoneNumber(field.value as string);
				return parsed?.nationalNumber || "";
			} catch {
				const valueStr = field.value as string;
				if (valueStr.startsWith("+")) {
					return valueStr.replace(/^\+\d+\s*/, "");
				}
				return valueStr;
			}
		}
		return "";
	};

	const [country, setCountry] = useState<Country>(getInitialCountry);
	const [phoneNumber, setPhoneNumber] = useState<string>(getInitialNumber);

	useEffect(() => {
		fieldValueRef.current = field.value;
	}, [field.value]);

	useEffect(() => {
		phoneNumberRef.current = phoneNumber;
	}, [phoneNumber]);

	const countryFromLocale = (): Country | null => {
		if (typeof navigator === "undefined") return null;
		const locales = navigator.languages?.length ? navigator.languages : [navigator.language];
		const validCountries = getCountries();

		for (const locale of locales) {
			const region = locale?.split(/[-_]/)[1]?.toUpperCase();
			if (region && validCountries.includes(region as Country)) {
				return region as Country;
			}
		}
		return null;
	};

	const countryFromName = (countryName: string): Country | null => {
		const normalizedName = countryName.trim().toLowerCase();
		if (!normalizedName) return null;

		for (const candidate of getCountries()) {
			const displayName = new Intl.DisplayNames(["en"], { type: "region" }).of(candidate);
			if (displayName?.toLowerCase() === normalizedName) {
				return candidate;
			}
		}
		return null;
	};

	const stripLocalLeadingZero = (value: string): string => {
		return value.trim().replace(/^0+(?=\d)/, "");
	};

	// Sync with form field value changes
	useEffect(() => {
		if (field.value) {
			try {
				const parsed = parsePhoneNumber(field.value as string);
				if (parsed?.country) {
					setCountry(parsed.country);
				}
				setPhoneNumber(parsed?.nationalNumber || "");
			} catch {
				const valueStr = field.value as string;
				if (valueStr.startsWith("+")) {
					setPhoneNumber(valueStr.replace(/^\+\d+\s*/, ""));
				} else {
					setPhoneNumber(valueStr);
				}
			}
		} else {
			setPhoneNumber("");
		}
	}, [field.value]);

	useEffect(() => {
		let cancelled = false;
		if (field.value || phoneNumber || hasUserSelectedCountry.current) return;

		void detectCountryFromGeolocation().then(({ countryCode, country: countryName }) => {
			if (
				cancelled ||
				fieldValueRef.current ||
				phoneNumberRef.current ||
				hasUserSelectedCountry.current
			) {
				return;
			}

			if (countryCode && getCountries().includes(countryCode as Country)) {
				setCountry(countryCode as Country);
				return;
			}

			if (countryName) {
				const detectedCountry = countryFromName(countryName);
				if (detectedCountry) {
					setCountry(detectedCountry);
					return;
				}
			}

			const localeCountry = countryFromLocale();
			if (localeCountry) {
				setCountry(localeCountry);
			}
		});

		return () => {
			cancelled = true;
		};
	}, [field.value, phoneNumber]);

	// Detect country from input if user types country code
	const detectCountryFromInput = (input: string): Country | null => {
		if (input.startsWith("+")) {
			// Try to parse the phone number to detect country
			try {
				const parsed = parsePhoneNumber(input);
				if (parsed?.country) {
					return parsed.country;
				}
			} catch {
				// Try to match by calling code
				const codeMatch = input.match(/^\+(\d{1,4})/);
				if (codeMatch) {
					const code = codeMatch[1];
					const countries = getCountries();
					for (const c of countries) {
						if (getCountryCallingCode(c) === code) {
							return c;
						}
					}
				}
			}
		}
		return null;
	};

	// Update form field when country or number changes
	const handleNumberChange = (num: string) => {
		// If number is empty, set to empty string (field is optional)
		if (!num || num.trim() === "") {
			field.onChange("");
			setPhoneNumber("");
			return;
		}

		// Check if user typed a country code
		const detectedCountry = detectCountryFromInput(num);
		let currentCountry = country;
		let nationalNumber = num;

		if (detectedCountry && detectedCountry !== country) {
			currentCountry = detectedCountry;
			setCountry(detectedCountry);
			// Extract national number if full number was typed
			try {
				const parsed = parsePhoneNumber(num);
				nationalNumber = parsed?.nationalNumber || stripLocalLeadingZero(num.replace(/^\+\d+\s*/, ""));
			} catch {
				nationalNumber = stripLocalLeadingZero(num.replace(/^\+\d+\s*/, ""));
			}
			setPhoneNumber(nationalNumber);
		} else {
			// Remove country code if user typed it but it matches current country
			nationalNumber = stripLocalLeadingZero(num.replace(/^\+\d+\s*/, ""));
			setPhoneNumber(nationalNumber);
		}
		
		const callingCode = getCountryCallingCode(currentCountry);
		const fullNumber = `+${callingCode}${nationalNumber}`;
		field.onChange(fullNumber);
	};

	const handleCountryChange = (newCountry: Country | undefined) => {
		hasUserSelectedCountry.current = true;
		const selectedCountry = newCountry || "US";
		setCountry(selectedCountry);
		if (phoneNumber) {
			const callingCode = getCountryCallingCode(selectedCountry);
			const fullNumber = `+${callingCode}${stripLocalLeadingZero(phoneNumber)}`;
			field.onChange(fullNumber);
		} else {
			// Just update country without number - set to empty string for optional field
			field.onChange("");
		}
	};

	const callingCode = getCountryCallingCode(country);

	// Component that uses formItemId inside FormItem context
	const InputWithId = () => {
		const { formItemId } = useFormField();
		return (
			<Input
				id={formItemId}
				type="tel"
				value={phoneNumber}
				onChange={(e) => handleNumberChange(e.target.value)}
				disabled={field.disabled || disabled}
				placeholder={placeholder || "Mobile number"}
				autoComplete="tel"
				className={cn(
					"flex-1 border-0 rounded-l-none rounded-r-md bg-transparent pl-12",
					FONT_SIZES.input,
					"text-foreground placeholder:text-muted-foreground focus-visible:ring-0"
				)}
			/>
		);
	};

	return (
		<FormItem intent={intent}>
			{label && <FormLabel intent={intent}>{label}</FormLabel>}

			<div className="border-standard flex h-11 w-full rounded-md border bg-[var(--color-bg-aqua-mist)] dark:bg-card">
				<CountrySelector
					value={country}
					onChange={handleCountryChange}
					disabled={field.disabled || disabled}
				/>
				<div className="relative flex-1 border-0">
					<span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-foreground">
						+{callingCode}
					</span>
					<InputWithId />
				</div>
			</div>
			<FormMessage intent={intent} />
		</FormItem>
	);
}

export default function MobileNumberInput({
	name,
	label,
	placeholder,
	value,
	intent,
	disabled,
}: TextInputProps) {
	const { control } = useFormContext();

	return (
		<FormField
			defaultValue={value}
			control={control}
			name={name}
			disabled={disabled}
			render={({ field }) => (
				<PhoneNumberInputInner
					field={{ 
						value: field.value as string | undefined, 
						onChange: (val: string) => field.onChange(val), 
						disabled: field.disabled 
					}}
					label={label}
					placeholder={placeholder}
					intent={intent}
					disabled={disabled}
				/>
			)}
		/>
	);
}
